import { useEffect, useState, useCallback } from 'react'
import VerificationFlow from './VerificationFlow'

/**
 * EmbedWrapper - Wrapper component for iframe embedding
 *
 * Handles:
 * - Reading configuration from URL query params
 * - Sending events to parent window via postMessage
 * - Receiving commands from parent window
 */
const EmbedWrapper = () => {
  const [config, setConfig] = useState({
    apiKey: null,
    theme: 'default',
    debugMode: false
  })
  const [userData, setUserData] = useState({
    nome: '',
    cpf: ''
  })
  const [isReady, setIsReady] = useState(false)

  // Parse URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)

    const newConfig = {
      apiKey: params.get('apiKey') || params.get('api_key'),
      theme: params.get('theme') || 'default',
      debugMode: params.get('debug') === 'true'
    }

    setConfig(newConfig)
    setIsReady(true)

    // Send ready event to parent
    sendToParent('LIVECHECK_READY', { config: newConfig })

    if (newConfig.debugMode) {
      console.log('[EMBED] Initialized with config:', newConfig)
    }
  }, [])

  // Send message to parent window
  const sendToParent = useCallback((type, data = {}) => {
    if (!window.parent || window.parent === window) {
      console.warn('[EMBED] No parent window found')
      return
    }

    const message = {
      source: 'livecheck-embed',
      type,
      data,
      timestamp: Date.now()
    }

    if (config.debugMode) {
      console.log('[EMBED] Sending to parent:', message)
    }

    // Send to all origins for now (can restrict later)
    window.parent.postMessage(message, '*')
  }, [config.debugMode])

  // Listen for messages from parent
  useEffect(() => {
    const handleMessage = (event) => {
      // Security: validate message structure
      if (!event.data || event.data.source !== 'livecheck-parent') {
        return
      }

      if (config.debugMode) {
        console.log('[EMBED] Received from parent:', event.data)
      }

      const { type, data } = event.data

      switch (type) {
        case 'RESET':
          // Reload iframe to reset state
          window.location.reload()
          break

        case 'UPDATE_CONFIG':
          setConfig(prev => ({ ...prev, ...data }))
          break

        case 'SET_USER_DATA':
          // Receive user data (nome, cpf) from parent page
          if (data.nome !== undefined || data.cpf !== undefined) {
            setUserData(prev => ({
              nome: data.nome !== undefined ? data.nome : prev.nome,
              cpf: data.cpf !== undefined ? data.cpf : prev.cpf
            }))
            if (config.debugMode) {
              console.log('[EMBED] User data updated:', { nome: data.nome, cpf: data.cpf })
            }
          }
          break

        case 'PING':
          sendToParent('PONG', { healthy: true })
          break

        default:
          console.warn('[EMBED] Unknown message type:', type)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [config.debugMode, sendToParent])

  // Handle verification events
  const handleStepChange = useCallback((step) => {
    sendToParent('STEP_CHANGE', { step })
  }, [sendToParent])

  const handleComplete = useCallback((result) => {
    if (config.debugMode) {
      console.log('[EMBED] Verification complete:', result)
    }

    sendToParent('VERIFICATION_COMPLETE', {
      success: true,
      result,
      message: 'Verificação concluída com sucesso'
    })
  }, [sendToParent, config.debugMode])

  const handleError = useCallback((error) => {
    if (config.debugMode) {
      console.error('[EMBED] Verification error:', error)
    }

    sendToParent('VERIFICATION_ERROR', {
      success: false,
      error: error.message || String(error),
      message: 'Erro na verificação'
    })
  }, [sendToParent, config.debugMode])

  const handleProgress = useCallback((progress) => {
    sendToParent('PROGRESS', { progress })
  }, [sendToParent])

  if (!isReady) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <div>Carregando LiveCheck...</div>
      </div>
    )
  }

  return (
    <div className="livecheck-embed">
      <VerificationFlow
        onComplete={handleComplete}
        onError={handleError}
        onStepChange={handleStepChange}
        onProgress={handleProgress}
        apiKey={config.apiKey}
        theme={config.theme}
        initialUserData={userData}
      />

      {config.debugMode && (
        <div style={{
          position: 'fixed',
          bottom: 10,
          right: 10,
          background: 'rgba(0,0,0,0.8)',
          color: '#0f0',
          padding: '5px 10px',
          borderRadius: 4,
          fontSize: 11,
          fontFamily: 'monospace',
          zIndex: 9999
        }}>
          DEBUG MODE | API: {config.apiKey ? '✓' : '✗'}
        </div>
      )}
    </div>
  )
}

export default EmbedWrapper
